<template>
  <div v-if="visible && currentQuestionnaire" class="feedback-banner" :class="{ 'sliding-out': isClosing }">
    <div class="feedback-content">
      <button class="close-button" @click="closeBanner" :aria-label="$t('common.actions.close')">
        <i class="pi pi-times"></i>
      </button>
      
      <div class="feedback-question">
        <h3>{{ currentQuestionnaire.question }}</h3>
      </div>
      
      <div class="feedback-options">
        <div 
          v-for="option in currentQuestionnaire.options" 
          :key="option.id"
          class="feedback-option"
          :class="{ 
            'selected': selectedOptionId === option.id,
            'input-option': option.type === 'input'
          }"
          @click="selectOption(option)"
        >
          <div v-if="option.image" class="option-image">
            <img :src="option.image" :alt="option.text" />
          </div>
          <div class="option-text">{{ option.text }}</div>
        </div>
      </div>
      
      <!-- Input field for custom answer -->
      <div v-if="showCustomInput" class="custom-input-section">
        <InputText 
          v-model="customAnswer"
          :placeholder="$t('feedback.customPlaceholder')"
          class="custom-input"
          @keyup.enter="submitFeedback"
        />
      </div>
      
      <div class="feedback-actions">
        <Button
          :label="$t('feedback.submit')"
          icon="pi pi-check"
          @click="submitFeedback"
          :disabled="!canSubmit"
          :loading="submitting"
          class="submit-button"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'petite-vue-i18n'
import ApiService from '../services/api.js'
import { useLinkflareAuth } from '../composables/useAuth.js'

export default {
  name: 'FeedbackBanner',
  components: {
    Button,
    InputText
  },
  setup() {
    const toast = useToast()
    const { t } = useI18n()
    const { getAuthToken } = useLinkflareAuth()
    
    const visible = ref(false)
    const currentQuestionnaire = ref(null)
    const selectedOptionId = ref(null)
    const customAnswer = ref('')
    const submitting = ref(false)
    const showCustomInput = ref(false)
    const isClosing = ref(false)
    
    const canSubmit = computed(() => {
      if (selectedOptionId.value === null) return false
      
      const selectedOption = currentQuestionnaire.value?.options.find(
        opt => opt.id === selectedOptionId.value
      )
      
      if (selectedOption?.type === 'input') {
        return customAnswer.value.trim().length > 0
      }
      
      return true
    })
    
    const loadQuestionnaire = async () => {
      try {
        const token = await getAuthToken()
        if (!token) {
          return
        }
        
        const response = await ApiService.getQuestionnaire(token)
        
        // Handle both response formats: { questionnaire: {...} } or direct questionnaire object
        let questionnaire = null
        
        if (response && response.questionnaire) {
          questionnaire = response.questionnaire
        } else if (response && response.id && response.question) {
          questionnaire = response
        }
        
        if (questionnaire) {
          // Check if user has already answered this questionnaire
          const answeredQuestionnaireIds = getAnsweredQuestionnaireIds()
          
          if (!answeredQuestionnaireIds.includes(questionnaire.id)) {
            currentQuestionnaire.value = questionnaire
            visible.value = true
          }
        }
      } catch (error) {
        console.error('🔍 FeedbackBanner: Failed to load questionnaire:', error)
        // Silently fail - banner is optional
      }
    }
    
    const getAnsweredQuestionnaireIds = () => {
      try {
        const stored = localStorage.getItem('linkflare_answered_questionnaires')
        return stored ? JSON.parse(stored) : []
      } catch (e) {
        console.warn('Failed to read answered questionnaires from localStorage', e)
        return []
      }
    }
    
    const markQuestionnaireAsAnswered = (questionnaireId) => {
      try {
        const answeredIds = getAnsweredQuestionnaireIds()
        if (!answeredIds.includes(questionnaireId)) {
          answeredIds.push(questionnaireId)
          localStorage.setItem('linkflare_answered_questionnaires', JSON.stringify(answeredIds))
        }
      } catch (e) {
        console.warn('Failed to save answered questionnaire to localStorage', e)
      }
    }
    
    const selectOption = (option) => {
      selectedOptionId.value = option.id
      
      if (option.type === 'input') {
        showCustomInput.value = true
      } else {
        showCustomInput.value = false
        customAnswer.value = ''
      }
    }
    
    const closeBanner = () => {
      // Start slide-out animation
      isClosing.value = true
      
      // Wait for animation to complete before hiding
      setTimeout(() => {
        visible.value = false
        isClosing.value = false
        
        // Mark as dismissed (not answered) in localStorage
        if (currentQuestionnaire.value) {
          markQuestionnaireAsAnswered(currentQuestionnaire.value.id)
        }
      }, 300) // Match animation duration
    }
    
    const submitFeedback = async () => {
      if (!canSubmit.value || !currentQuestionnaire.value) return
      
      try {
        submitting.value = true
        const token = await getAuthToken()
        if (!token) return
        
        const selectedOption = currentQuestionnaire.value.options.find(
          opt => opt.id === selectedOptionId.value
        )
        
        const feedbackData = {
          question_id: currentQuestionnaire.value.id,
          option_id: selectedOption.type === 'choice' ? selectedOptionId.value : null,
          custom_answer: selectedOption.type === 'input' ? customAnswer.value : null
        }
        
        await ApiService.submitFeedback(feedbackData, token)
        
        // Mark questionnaire as answered
        markQuestionnaireAsAnswered(currentQuestionnaire.value.id)
        
        toast.add({
          severity: 'success',
          summary: t('feedback.toasts.thanksTitle'),
          detail: t('feedback.toasts.thanksDetail'),
          life: 3000
        })
        
        // Start slide-out animation
        isClosing.value = true
        
        // Wait for animation to complete before hiding
        setTimeout(() => {
          visible.value = false
          isClosing.value = false
        }, 300) // Match animation duration
      } catch (error) {
        console.error('Failed to submit feedback:', error)
        toast.add({
          severity: 'error',
          summary: t('common.toasts.errorTitle'),
          detail: t('feedback.toasts.failedDetail'),
          life: 3000
        })
      } finally {
        submitting.value = false
      }
    }
    
    onMounted(() => {
      // Temporarily disabled until the questionnaire endpoint stops returning 404.
      // loadQuestionnaire()
    })
    
    return {
      visible,
      currentQuestionnaire,
      selectedOptionId,
      customAnswer,
      submitting,
      showCustomInput,
      isClosing,
      canSubmit,
      selectOption,
      closeBanner,
      submitFeedback
    }
  }
}
</script>

<style scoped>
.feedback-banner {
  background: linear-gradient(135deg, var(--accent-color) 0%, var(--accent-light) 100%);
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 2px 8px var(--shadow-medium);
  animation: slideDown 0.3s ease-out;
}

.feedback-banner.sliding-out {
  animation: slideUp 0.3s ease-in forwards;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
}

.feedback-content {
  position: relative;
  max-width: 1600px;
  margin: 0 auto;
  padding: 1.5rem 2rem;
}

.close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.feedback-question {
  text-align: center;
  margin-bottom: 1.5rem;
}

.feedback-question h3 {
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.feedback-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.feedback-option {
  background: #fff;
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.feedback-option:hover {
  background: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--shadow-medium);
}

.feedback-option.selected {
  border-color: white;
  background: white;
  box-shadow: 0 0 0 2px var(--accent-color);
}

.option-image {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
}

.option-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.option-text {
  font-weight: 500;
  color: var(--text-primary);
  text-align: center;
  font-size: 0.95rem;
}

.custom-input-section {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.custom-input {
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
}

.custom-input:focus {
  border-color: white;
  background: white;
  outline: none;
}

.feedback-actions {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.submit-button {
  background: white !important;
  color: var(--accent-color) !important;
  border: none !important;
  padding: 0.75rem 2rem !important;
  font-weight: 600 !important;
  font-size: 1rem !important;
}

.submit-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--shadow-dark) !important;
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 768px) {
  .feedback-content {
    padding: 1rem;
  }
  
  .feedback-question h3 {
    font-size: 1.1rem;
  }
  
  .feedback-options {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.75rem;
  }
  
  .option-image {
    width: 60px;
    height: 60px;
  }
  
  .option-text {
    font-size: 0.875rem;
  }
  
  .close-button {
    top: 0.5rem;
    right: 0.5rem;
    width: 28px;
    height: 28px;
  }
}
</style>
